import { Decimal } from 'decimal.js';
/**
 * Decimal utilities — the ONLY sanctioned numeric substrate for money math.
 * (Product law: all math in the shared engine via decimal.js, never in SQL,
 * never in floats.)
 *
 * DB scales (ARCHITECTURE): rates numeric(14,6), amounts numeric(14,2),
 * qty numeric(14,4). DTOs carry these as STRINGS; percentages travel as
 * plain JS numbers and are converted with `dec()` before any math.
 */
/** Configured Decimal constructor: generous precision, ROUND_HALF_UP, no exponent notation in our ranges. */
export const D = Decimal.clone({
    precision: 40,
    rounding: Decimal.ROUND_HALF_UP,
    toExpNeg: -30,
    toExpPos: 30,
});
export const MONEY_DP = 2;
export const RATE_DP = 6;
export const QTY_DP = 4;
/**
 * Construct a finite Decimal. Throws on NaN/Infinity/garbage — silent NaN
 * propagation into a hashed money document is the failure mode this guards.
 */
export function dec(value) {
    if (typeof value === 'number' && !Number.isFinite(value)) {
        throw new TypeError(`dec: non-finite number ${value}`);
    }
    const d = new D(value instanceof Decimal ? value.toString() : value);
    if (!d.isFinite()) {
        throw new TypeError(`dec: non-finite value ${String(value)}`);
    }
    return d;
}
/** Round to `dp` decimal places, half-up. */
export function roundTo(value, dp) {
    return dec(value).toDecimalPlaces(dp, Decimal.ROUND_HALF_UP);
}
/** Round to money scale (2dp, half-up) — the line extended-price rounding boundary. */
export function roundMoney(value) {
    return roundTo(value, MONEY_DP);
}
/** Fixed 2dp money string, e.g. "1234.50". */
export function toMoneyString(value) {
    return roundMoney(value).toFixed(MONEY_DP);
}
/** Rate string with at most 6dp (trailing zeros trimmed; "12.5" stays "12.5"). */
export function toRateString(value) {
    return roundTo(value, RATE_DP).toString();
}
/** Quantity string with at most 4dp (trailing zeros trimmed). */
export function toQtyString(value) {
    return roundTo(value, QTY_DP).toString();
}
/** Sum a list of decimal inputs (0 for an empty list). */
export function sumDecimals(values) {
    let acc = new D(0);
    for (const v of values)
        acc = acc.plus(dec(v));
    return acc;
}
/** Convert a percentage (e.g. 15 => 0.15) to a fraction Decimal. */
export function pctToFraction(pct) {
    return dec(pct).div(100);
}
export { Decimal };
//# sourceMappingURL=decimal.js.map